import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../lib/api';
import { formatPrice } from '../lib/currency';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, Cell } from 'recharts';
import { Loader2, Ban, ArrowLeft, BarChart3, Eye, MousePointerClick, ShieldQuestion, TrendingDown, Info, FlaskConical, Percent } from 'lucide-react';

const FUNNEL_LABELS = {
  smart_fare_impression: 'Impressions',
  smart_fare_open: 'Card opens',
  smart_fare_detail_view: 'Detail views',
  smart_fare_warning_view: 'Warning views',
  smart_fare_acknowledgement: 'Acknowledgements',
  smart_fare_partner_click: 'Partner clicks',
};

export default function FareAnalytics() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [denied, setDenied] = useState(false);
  const [data, setData] = useState(null);
  const [includeDemo, setIncludeDemo] = useState(false);
  const [breakdown, setBreakdown] = useState('type');

  useEffect(() => { document.title = 'Admin · Fare Analytics | FlightDeals.in'; if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);
  useEffect(() => {
    if (!user) return;
    setData(null);
    api.fiAnalytics({ include_demo: includeDemo, breakdown }).then(({ data }) => setData(data))
      .catch((e) => { if (e?.response?.status === 403) setDenied(true); });
  }, [user, includeDemo, breakdown]);

  if (loading) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;
  if (denied) return <Layout><div className="max-w-lg mx-auto py-24 text-center"><Ban className="h-10 w-10 mx-auto mb-2 text-amber-500" /><h1 className="font-display text-2xl font-semibold">Staff access required</h1></div></Layout>;

  const funnelData = data ? Object.entries(data.funnel).map(([k, v]) => ({ step: FUNNEL_LABELS[k] || k, count: v })) : [];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/admin/fare-intelligence')} className="text-sm text-muted-foreground hover:text-accent flex items-center gap-1 mb-3" data-testid="fa-back"><ArrowLeft className="h-4 w-4" /> Back to Fare Intelligence</button>
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><BarChart3 className="h-6 w-6 text-accent" /> Smart Fare Analytics</h1>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm"><Switch checked={includeDemo} onCheckedChange={setIncludeDemo} data-testid="fa-include-demo" /><span className="flex items-center gap-1"><FlaskConical className="h-3.5 w-3.5" />Include demo</span></label>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Engagement funnel & potential savings. Demo/test data is excluded by default.</p>

        {!data ? <div className="grid gap-3 grid-cols-2 md:grid-cols-4 mb-6">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div> : (
          <>
            <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-3 text-xs text-muted-foreground flex items-start gap-2 mb-5" data-testid="fa-disclaimer">
              <Info className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" /> {data.disclaimer}
            </div>

            <div className="grid gap-3 grid-cols-2 md:grid-cols-4 mb-6" data-testid="fa-kpis">
              <KPI icon={Eye} label="Impressions" value={data.funnel.smart_fare_impression} />
              <KPI icon={Percent} label="Open CTR" value={`${data.rates.ctr}%`} />
              <KPI icon={ShieldQuestion} label="Ack rate" value={`${data.rates.ack_rate}%`} />
              <KPI icon={MousePointerClick} label="Partner CTR" value={`${data.rates.partner_ctr}%`} />
              <KPI icon={TrendingDown} label="Potential savings shown" value={formatPrice(data.potential_savings_shown, 'INR')} sub="POTENTIAL — not revenue" wide />
              <KPI icon={MousePointerClick} label="Sponsored partner clicks" value={data.sponsored_partner_clicks} />
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <Card className="p-5 rounded-2xl" data-testid="fa-funnel">
                <h3 className="font-semibold mb-4 text-sm">Engagement funnel</h3>
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={funnelData} layout="vertical" margin={{ left: 30 }}>
                      <XAxis type="number" hide /><YAxis type="category" dataKey="step" width={120} tick={{ fontSize: 12 }} />
                      <RTooltip cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                      <Bar dataKey="count" radius={[0, 6, 6, 0]}>{funnelData.map((_, i) => <Cell key={i} fill="hsl(173 62% 36%)" />)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="p-5 rounded-2xl" data-testid="fa-breakdown">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm">Opportunity breakdown</h3>
                  <Select value={breakdown} onValueChange={setBreakdown}>
                    <SelectTrigger className="w-40 h-8" data-testid="fa-breakdown-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{[['type', 'By type'], ['risk', 'By risk'], ['provider', 'By provider'], ['destination', 'By destination'], ['origin', 'By origin']].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {data.breakdown.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No data.</p> : (
                  <table className="w-full text-sm" data-testid="fa-breakdown-table">
                    <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2">{data.breakdown_by}</th><th className="text-right">Count</th><th className="text-right">Avg score</th><th className="text-right">Avg saving</th></tr></thead>
                    <tbody>
                      {data.breakdown.map((r) => (
                        <tr key={r.key} className="border-b border-border last:border-0">
                          <td className="py-2 font-medium">{r.key}</td>
                          <td className="text-right tabular">{r.count}</td>
                          <td className="text-right tabular">{r.avg_score}</td>
                          <td className="text-right tabular text-emerald-700">{formatPrice(r.avg_saving, 'INR')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

const KPI = ({ icon: Icon, label, value, sub, wide }) => (
  <Card className={`p-4 rounded-2xl ${wide ? 'col-span-2' : ''}`} data-testid={`fa-kpi-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Icon className="h-4 w-4 text-accent" /> {label}</div>
    <p className="text-2xl font-semibold tabular">{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
  </Card>
);
